import type { TOC } from '@ember/component/template-only';

// ---------------------------------------------------------------------------
// StepCard – onboarding/progress step card with number indicator
// ---------------------------------------------------------------------------

interface StepCardSignature {
  Args: {
    /** Step number to display (1, 2, 3, etc.) */
    step: number;
    /** Main title for the step */
    title: string;
    /** Description text explaining the step */
    description: string;
    /** Whether the step is completed (default: false) */
    completed?: boolean;
    /** Whether the step is the current/active step (default: false) */
    active?: boolean;
  };
  Blocks: {
    /** Action button or content to display on the right side */
    action?: [];
  };
  Element: HTMLDivElement;
}

/**
 * StepCard - Onboarding/progress step card with number indicator
 *
 * Displays a numbered step with title, description, and optional action.
 * Supports completed and active states for visual feedback.
 *
 * @example Basic usage
 * ```gts
 * import StepCard from 'tio-reimagined/components/step-card';
 *
 * <StepCard
 *   @step={{1}}
 *   @title="Upload your loan documents"
 *   @description="Upload loan statements or NSLDS file to see your balances"
 * >
 *   <:action>
 *     <Button @intent="primary" @appearance="outlined">Upload</Button>
 *   </:action>
 * </StepCard>
 * ```
 *
 * @example Completed step
 * ```gts
 * <StepCard @step={{1}} @title="Profile completed" @description="Your career profile is set up" @completed={{true}} />
 * ```
 */
const StepCard: TOC<StepCardSignature> = <template>
  <div
    data-test-step-card
    class="flex items-center gap-4 p-4 rounded-xl border-2
      {{if
        @completed
        'bg-emerald-50 border-emerald-200'
        (if @active 'bg-indigo-50 border-indigo-200' 'bg-gray-50 border-dashed border-gray-200')
      }}"
    ...attributes
  >
    <div
      data-test-step-card-indicator
      class="w-8 h-8 rounded-full flex items-center justify-center font-medium shrink-0
        {{if
          @completed
          'bg-emerald-500 text-white'
          (if @active 'bg-indigo-500 text-white' 'bg-gray-200 text-gray-500')
        }}"
    >
      {{#if @completed}}
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
            d="M5 13l4 4L19 7"
          />
        </svg>
      {{else}}
        {{@step}}
      {{/if}}
    </div>
    <div class="flex-1 min-w-0">
      <div
        data-test-step-card-title
        class="font-medium
          {{if @completed 'text-emerald-700' (if @active 'text-indigo-900' 'text-gray-900')}}"
      >
        {{@title}}
      </div>
      <div
        data-test-step-card-description
        class="text-sm
          {{if @completed 'text-emerald-600' (if @active 'text-indigo-600' 'text-gray-500')}}"
      >
        {{@description}}
      </div>
    </div>
    {{#if (has-block "action")}}
      <div data-test-step-card-action class="shrink-0">
        {{yield to="action"}}
      </div>
    {{/if}}
  </div>
</template>;

export default StepCard;

export type { StepCardSignature };
